import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Plus, CalendarIcon, Loader2, Sparkles, Tag } from "lucide-react";

// Schema for promo code creation
const promoCodeSchema = z.object({
  code: z
    .string()
    .min(4, "Code must be at least 4 characters")
    .max(20, "Code must be at most 20 characters")
    .regex(/^[A-Z0-9]+$/, "Code must be alphanumeric and uppercase")
    .transform((val) => val.toUpperCase()),
  description: z.string().optional(),
  discountPercent: z
    .number()
    .min(5, "Discount must be at least 5%")
    .max(50, "Discount must be at most 50%")
    .default(10),
  maxRedemptions: z.number().min(1).optional().or(z.literal("")),
  expiresAt: z.date().optional(),
});

type PromoCodeFormData = z.infer<typeof promoCodeSchema>;

interface PromoCodeDialogProps {
  trigger?: React.ReactNode;
  onSuccess?: () => void;
}

export default function PromoCodeDialog({ trigger, onSuccess }: PromoCodeDialogProps) {
  const [open, setOpen] = useState(false);
  const { toast } = useToast();
  
  const form = useForm<PromoCodeFormData>({
    resolver: zodResolver(promoCodeSchema),
    defaultValues: {
      code: "",
      description: "",
      discountPercent: 10,
      maxRedemptions: undefined,
      expiresAt: undefined,
    },
  });

  const createPromoCodeMutation = useMutation({
    mutationFn: (data: PromoCodeFormData) =>
      apiRequest("/api/influencer/promo-codes", "POST", {
        ...data,
        maxRedemptions: data.maxRedemptions || undefined,
        expiresAt: data.expiresAt?.toISOString(),
      }),
    onSuccess: () => {
      toast({
        title: "Promo Code Created",
        description: `Your promo code ${form.getValues("code")} has been created successfully.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/influencer/promo-codes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/influencer/dashboard"] });
      form.reset();
      setOpen(false);
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Creation Failed",
        description: error?.message || "Failed to create promo code",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (data: PromoCodeFormData) => {
    createPromoCodeMutation.mutate(data);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    form.setValue("code", value);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button data-testid="button-create-promo-code" className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
            <Plus className="w-4 h-4 mr-2" />
            Create Promo Code
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-purple-500" />
            Create New Promo Code
          </DialogTitle>
          <DialogDescription>
            Create a unique promo code for your audience. They'll get a discount, and you'll earn commission on their purchases.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Promo Code</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      data-testid="input-promo-code"
                      placeholder="SAVE20"
                      onChange={handleCodeChange}
                      className="uppercase font-mono"
                      maxLength={20}
                    />
                  </FormControl>
                  <FormDescription>
                    4-20 characters, alphanumeric only (auto-uppercase)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      data-testid="textarea-promo-description"
                      placeholder="Special offer for my followers..."
                      className="resize-none"
                      rows={2}
                    />
                  </FormControl>
                  <FormDescription>
                    Internal note about this promo code
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="discountPercent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Discount Percentage: {field.value}%</FormLabel>
                  <FormControl>
                    <div className="flex items-center space-x-4">
                      <Slider
                        data-testid="slider-discount-percent"
                        min={5}
                        max={50}
                        step={5}
                        value={[field.value]}
                        onValueChange={(value) => field.onChange(value[0])}
                        className="flex-1"
                      />
                      <span className="w-12 text-right font-semibold">
                        {field.value}%
                      </span>
                    </div>
                  </FormControl>
                  <FormDescription>
                    The discount your followers will receive
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="maxRedemptions"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Max Redemptions (Optional)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      data-testid="input-max-redemptions"
                      type="number"
                      placeholder="Unlimited"
                      min={1}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : "")}
                    />
                  </FormControl>
                  <FormDescription>
                    Leave empty for unlimited redemptions
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="expiresAt"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Expiry Date (Optional)</FormLabel>
                  <Popover>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          data-testid="button-expiry-date"
                          variant="outline"
                          className={cn(
                            "w-full pl-3 text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                        >
                          {field.value ? (
                            format(field.value, "PPP")
                          ) : (
                            <span>Pick a date</span>
                          )}
                          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={field.value}
                        onSelect={field.onChange}
                        disabled={(date) =>
                          date < new Date() || date < new Date("1900-01-01")
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  <FormDescription>
                    When this promo code will expire
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={createPromoCodeMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                data-testid="button-submit-promo-code"
                type="submit"
                disabled={createPromoCodeMutation.isPending}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white"
              >
                {createPromoCodeMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Code
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}